// ── LoginScreen ──────────────────────────────────────────────────────────────
// The Google/email sign-in UI. Used two ways:
//   • standalone, by AuthGate, for anyone who's already finished onboarding
//     once and is now signed out (reinstall, manual sign-out, etc.)
//   • as the page right after OnboardingFlow's preview pages (before the quiz)
//     for a brand-new install — see the "auth" phase in OnboardingFlow.jsx.
//     `onBack` is only passed in that second case; it now only switches on
//     the first-run greeting (the back chevron was removed, Jade 2026-10-11).
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Cloud, ShieldCheck, WifiOff } from 'lucide-react';
import {
  signInWithGoogle, signInWithGoogleViaRedirect,
  signInWithEmail, signUpWithEmail, resetPassword, friendlyAuthError,
} from './auth';

import { ONBOARDING_VARS, OB_BLUE, OB_VIOLET, OB_INK, OB_PINK, OB_PINK_TEXT, OB_BTN_PINK, OB_BTN_GRADIENT } from '../theme/onboardingPalette';

// Same blue-violet palette as the onboarding pages (pink only as a small accent).
const ACCENT = OB_VIOLET;
const ACCENT_DARK = OB_INK;
const SOFT_TEXT = '#333333';
const LINE = '#C9D3F3';
// Buttons blend blue -> lavender -> pink, the three colours of the app logo
// (Jade, 2026-10-11: "more pinkish, to match the logo"). Black text stays readable.
const BTN_PINK = OB_BTN_PINK;
const BTN_GRADIENT = OB_BTN_GRADIENT;
const BG = ONBOARDING_VARS['--c-hero-gradient'];

// Extra decoration (Jade, 2026-10-11: "add more UI elements"): layered waves in
// the logo's colours along the bottom (echoing its swirls) and a faint dot grid
// at the top — round blurred blobs were tried first and dropped — plus a glow
// round the logo (its sparkles were removed, Jade), three perk chips, the form in a card, and a Terms/Privacy
// line. Pure decoration — no behaviour changed.
const WAVES = [
  { color: OB_BLUE, opacity: 0.55, d: 'M0,70 C80,30 160,30 240,60 C300,82 350,70 400,46 L400,160 L0,160 Z' },
  { color: OB_VIOLET, opacity: 0.6, d: 'M0,98 C70,72 150,70 220,92 C290,114 340,108 400,84 L400,160 L0,160 Z' },
  { color: BTN_PINK, opacity: 0.65, d: 'M0,128 C90,104 170,110 250,124 C320,136 360,128 400,114 L400,160 L0,160 Z' },
];
const DOT_GRID = {
  position: 'absolute', top: 0, left: 0, right: 0, height: 260,
  backgroundImage: `radial-gradient(${OB_VIOLET} 1.4px, transparent 1.6px)`,
  backgroundSize: '18px 18px',
  WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
  maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
};
const PERKS = [
  { Icon: Cloud, label: 'Syncs' },
  { Icon: ShieldCheck, label: 'Backed up' },
  { Icon: WifiOff, label: 'Works offline' },
];
const CARD = {
  width: 320, maxWidth: '92vw', boxSizing: 'border-box',
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.9)', borderRadius: 24, padding: '20px 16px',
  boxShadow: '0 10px 36px rgba(143,166,236,0.22)',
};

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
      <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
    </svg>
  );
}

// Seconds before "Resend" works again on the reset screen.
const RESEND_WAIT_S = 30;

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '12px 14px', fontSize: 15,
  border: `1px solid ${LINE}`, borderRadius: 12,
  outline: 'none', background: '#fff', color: OB_INK,
  fontFamily: 'inherit',
};

function EyeIcon({ off }) {
  return off ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export default function LoginScreen({ onBack }) {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'reset'
  // Forgot-password screen (Jade, 2026-10-11): its own step instead of a
  // one-tap send from the login form. `resetSentTo` = the address the link
  // went to (shows the "Check your inbox" view); `resendIn` = seconds left
  // before Resend works again.
  const [resetSentTo, setResetSentTo] = useState(null);
  const [resendIn, setResendIn] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [popupBlocked, setPopupBlocked] = useState(false);

  const run = async (fn) => {
    setBusy(true); setError(null); setNotice(null); setPopupBlocked(false);
    try { await fn(); }
    catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ClarityAuth] sign-in failed', e?.code, e?.message, e);
      // A blocked pop-up gets its own actionable banner instead of a generic error.
      if (e?.code === 'auth/popup-blocked') setPopupBlocked(true);
      else setError(friendlyAuthError(e));
      setBusy(false);
    }
  };

  const handleGoogle = () => run(async () => { await signInWithGoogle(); });
  const handleRedirect = () => run(async () => { await signInWithGoogleViaRedirect(); });

  const handleEmail = (e) => {
    e.preventDefault();
    run(async () => {
      if (mode === 'signup') await signUpWithEmail(email, password, name);
      else await signInWithEmail(email, password);
      // onAuthStateChanged flips the gate on success.
    });
  };

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const openReset = () => { setMode('reset'); setResetSentTo(null); setError(null); setNotice(null); };
  const backToLogin = () => { setMode('signin'); setResetSentTo(null); setError(null); setNotice(null); };

  const sendReset = (e) => {
    e?.preventDefault?.();
    if (!email.trim()) { setError('Enter the email you signed up with.'); return; }
    run(async () => {
      // No account under that address: show the same "Check your inbox" screen
      // anyway, so the form can't be used to find out who has an account (and
      // Firebase's email-enumeration protection hides this case on its own).
      try { await resetPassword(email); }
      catch (err) { if (err?.code !== 'auth/user-not-found') throw err; }
      setBusy(false);
      setResetSentTo(email.trim());
      setResendIn(RESEND_WAIT_S);
    });
  };

  // Tapping a field brings the form's main button up above the keyboard (Jade,
  // 2026-10-11: the keyboard covered "Send reset link"). The keyboard takes a
  // moment to open and shrink the screen, so scroll once it has, and again on
  // each resize while the field is still focused.
  const keepFormAboveKeyboard = (e) => {
    const field = e.target;
    if (!(field instanceof HTMLInputElement)) return;
    const lift = () => {
      if (document.activeElement !== field) return;
      const target = field.form?.querySelector('button[type="submit"]') || field;
      target.style.scrollMarginBottom = '24px'; // a little room above the keyboard
      target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    const vv = window.visualViewport;
    const onResize = () => lift();
    window.addEventListener('resize', onResize);
    vv?.addEventListener('resize', onResize);
    const t1 = setTimeout(lift, 350);
    const t2 = setTimeout(lift, 800);
    field.addEventListener('blur', () => {
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener('resize', onResize);
      vv?.removeEventListener('resize', onResize);
    }, { once: true });
  };

  // Portalled straight to document.body, full-bleed and fixed — used both
  // standalone (AuthGate) and as OnboardingFlow's last page, and in that
  // second case it used to render inline wherever OnboardingFlow sits in the
  // tree instead of taking over the screen, leaving the Habits tab visible
  // behind/beside it. Every other onboarding screen already portals (see
  // OnboardingChrome.jsx's screenWrap) — this brings LoginScreen in line,
  // same zIndex so it isn't hidden behind those either.
  return createPortal(
    <div onFocusCapture={keepFormAboveKeyboard} style={{
      ...ONBOARDING_VARS,
      position: 'fixed', inset: 0, zIndex: 999999, overflowY: 'auto',
      display: 'flex', flexDirection: 'column',
      // 'safe': once the keyboard shortens the screen and the content no longer
      // fits, it starts at the top and scrolls, instead of spilling off both ends.
      alignItems: 'center', justifyContent: 'safe center', gap: 18,
      background: BG, padding: 24, textAlign: 'center',
      fontFamily: 'Quicksand, Nunito, system-ui, sans-serif',
    }}>
      <div aria-hidden="true" style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <div style={DOT_GRID} />
        <svg viewBox="0 0 400 160" preserveAspectRatio="none"
          style={{ position: 'absolute', left: 0, bottom: 0, width: '100%', height: 'min(150px, 12vh)' }}>
          {WAVES.map((w, i) => <path key={i} d={w.d} fill={w.color} opacity={w.opacity} />)}
        </svg>
      </div>
      {/* No back button (Jade, 2026-10-11): once on sign-in there's no going
          back to the preview pages. onBack is still passed by onboarding and
          is what shows the first-run greeting below. */}

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, position: 'relative', zIndex: 1 }}>
        <div style={{ position: 'relative', width: 84, height: 84 }}>
          <div style={{
            position: 'absolute', inset: -10, borderRadius: 30,
            background: `linear-gradient(135deg, ${OB_BLUE}, ${OB_VIOLET}, ${BTN_PINK})`,
            filter: 'blur(14px)', opacity: 0.7,
          }} />
          <img
            src="/app-logo.png"
            alt="Clarity"
            style={{ position: 'relative', width: 84, height: 84, borderRadius: 22, objectFit: 'cover', border: '3px solid #fff', boxSizing: 'border-box', boxShadow: '0 6px 18px rgba(214,140,180,0.3)' }}
          />
        </div>
        {/* Greeting — only on the first-run page (onBack is passed there). A
            returning user who is signed out just sees the app name. */}
        {onBack && mode === 'signin' && (
          <>
            <p style={{ margin: '14px 0 0', color: OB_INK, fontSize: 22, fontWeight: 700, lineHeight: 1.25, maxWidth: 290 }}>
              A fresh start begins here
            </p>
            <p style={{ margin: 0, color: SOFT_TEXT, fontSize: 14, maxWidth: 290 }}>
              Sign in to save your progress.
            </p>
          </>
        )}
        {mode === 'signup' && (
          <p style={{ margin: '12px 0 0', color: SOFT_TEXT, fontSize: 14, maxWidth: 290 }}>
            Create an account to sync across your devices.
          </p>
        )}
      </div>

      {mode !== 'reset' && (
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 330, position: 'relative', zIndex: 1 }}>
        {PERKS.map(({ Icon, label }) => (
          <span key={label} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, color: OB_INK, whiteSpace: 'nowrap',
            background: 'rgba(255,255,255,0.75)', border: `1px solid ${LINE}`,
          }}>
            <Icon size={13} color="#8FA6EC" strokeWidth={2.4} /> {label}
          </span>
        ))}
      </div>
      )}

      {mode === 'reset' ? (
      <div style={{ ...CARD, position: 'relative', zIndex: 1, textAlign: 'left', alignItems: 'stretch' }}>
        {resetSentTo ? (
          <>
            <div style={{ textAlign: 'center', fontSize: 34, lineHeight: 1 }} aria-hidden="true">📩</div>
            <p style={{ margin: 0, textAlign: 'center', color: OB_INK, fontSize: 18, fontWeight: 700 }}>Check your inbox</p>
            <p style={{ margin: 0, color: SOFT_TEXT, fontSize: 13.5, lineHeight: 1.5 }}>
              If an account uses <strong style={{ color: OB_INK, wordBreak: 'break-all' }}>{resetSentTo}</strong>, we've
              sent it a link to set a new password. Open it, pick a new password, then come back and log in.
            </p>
            <p style={{ margin: 0, color: SOFT_TEXT, fontSize: 12.5, lineHeight: 1.5 }}>
              Don't see it after a few minutes? Check your <strong>Spam</strong> or <strong>Promotions</strong> folder.
            </p>
            <button type="button" onClick={backToLogin}
              style={{
                width: '100%', padding: '12px 18px', fontSize: 15, fontWeight: 700,
                color: '#000', background: BTN_GRADIENT, border: 'none', borderRadius: 999,
                cursor: 'pointer', boxShadow: '0 4px 16px rgba(143,166,236,0.4)', fontFamily: 'inherit',
              }}>
              Back to log in
            </button>
            <button type="button" onClick={sendReset} disabled={busy || resendIn > 0}
              style={{
                background: 'none', border: 'none', padding: 4, fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                color: resendIn > 0 ? SOFT_TEXT : OB_PINK_TEXT, cursor: busy || resendIn > 0 ? 'default' : 'pointer',
                textDecoration: resendIn > 0 ? 'none' : 'underline', opacity: busy ? 0.6 : 1,
              }}>
              {resendIn > 0 ? `Resend email in ${resendIn}s` : 'Resend email'}
            </button>
          </>
        ) : (
          <form onSubmit={sendReset} style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
            <p style={{ margin: 0, textAlign: 'center', color: OB_INK, fontSize: 18, fontWeight: 700 }}>Reset your password</p>
            <p style={{ margin: 0, color: SOFT_TEXT, fontSize: 13.5, lineHeight: 1.5 }}>
              Enter the email you signed up with and we'll send you a link to set a new password.
            </p>
            <input
              style={inputStyle} type="email" placeholder="Email" required
              value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
            />
            <button type="submit" disabled={busy}
              style={{
                width: '100%', padding: '12px 18px', fontSize: 15, fontWeight: 700,
                color: '#000', background: BTN_GRADIENT, border: 'none', borderRadius: 999,
                cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
                boxShadow: '0 4px 16px rgba(143,166,236,0.4)', fontFamily: 'inherit',
              }}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
            <p style={{ margin: 0, color: SOFT_TEXT, fontSize: 12, lineHeight: 1.5 }}>
              Signed up with Google? You don't need a password: go back and tap <strong>Continue with Google</strong>.
            </p>
            <button type="button" onClick={backToLogin}
              style={{ background: 'none', border: 'none', padding: 4, color: OB_PINK_TEXT, fontSize: 13, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' }}>
              Back to log in
            </button>
          </form>
        )}
        {error && <p style={{ color: '#c0392b', fontSize: 13, margin: 0, textAlign: 'center' }}>{error}</p>}
      </div>
      ) : (
      <div style={{ ...CARD, position: 'relative', zIndex: 1 }}>
      {/* Google */}
      <button
        onClick={handleGoogle}
        disabled={busy}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          width: '100%',
          background: '#fff', color: '#000',
          border: `1px solid ${LINE}`, borderRadius: 999,
          padding: '12px 18px', fontSize: 15, fontWeight: 600,
          cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
          boxShadow: '0 4px 16px rgba(143,166,236,0.25)', fontFamily: 'inherit',
        }}
      >
        <GoogleGlyph /> Continue with Google
      </button>

      {/* Pop-up blocked banner — actionable steps to re-enable + fallbacks */}
      {popupBlocked && (
        <div style={{
          width: '100%', boxSizing: 'border-box', textAlign: 'left',
          background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14,
          padding: '14px 16px', boxShadow: '0 4px 16px rgba(143,166,236,0.25)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 18 }}>🚫</span>
            <strong style={{ fontSize: 14, color: ACCENT_DARK }}>Pop-up was blocked</strong>
          </div>
          <p style={{ margin: '0 0 8px', fontSize: 12.5, color: OB_INK, lineHeight: 1.5 }}>
            Your browser blocked the sign-in pop-up. Allowing it gives the most
            reliable login that <em>stays</em> signed in:
          </p>
          <ol style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 12.5, color: OB_INK, lineHeight: 1.6 }}>
            <li>Tap the <strong>🔒 / ⓘ</strong> icon beside the address bar</li>
            <li>Open <strong>Site settings</strong> → <strong>Pop-ups and redirects</strong></li>
            <li>Switch it to <strong>Allow</strong></li>
            <li>Come back and tap <strong>Try again</strong></li>
          </ol>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleGoogle} disabled={busy}
              style={{
                flex: 1, padding: '10px 12px', fontSize: 13, fontWeight: 700,
                color: '#000', background: BTN_GRADIENT, border: 'none', borderRadius: 999,
                cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: 'inherit',
              }}>
              Try again
            </button>
            <button
              onClick={handleRedirect} disabled={busy}
              style={{
                flex: 1, padding: '10px 12px', fontSize: 13, fontWeight: 700,
                color: ACCENT_DARK, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 999,
                cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: 'inherit',
              }}>
              Use redirect
            </button>
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 11, color: SOFT_TEXT, lineHeight: 1.45 }}>
            “Use redirect” works without pop-ups, but on some mobile browsers the
            session may not persist. Allowing pop-ups is the durable fix.
          </p>
        </div>
      )}

      {/* divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', color: SOFT_TEXT, fontSize: 12 }}>
        <span style={{ flex: 1, height: 1, background: LINE }} />
        or
        <span style={{ flex: 1, height: 1, background: LINE }} />
      </div>

      {/* Email / password */}
      <form onSubmit={handleEmail} style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        {mode === 'signup' && (
          <input
            style={inputStyle} type="text" placeholder="Your name (optional)"
            value={name} onChange={(e) => setName(e.target.value)} autoComplete="name"
          />
        )}
        <input
          style={inputStyle} type="email" placeholder="Email" required
          value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
        />
        <div style={{ position: 'relative' }}>
          <input
            style={{ ...inputStyle, paddingRight: 44 }}
            type={showPassword ? 'text' : 'password'} placeholder="Password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)',
              background: 'none', border: 'none', padding: 4, margin: 0,
              color: SOFT_TEXT, display: 'flex', alignItems: 'center', cursor: 'pointer',
            }}
          >
            <EyeIcon off={showPassword} />
          </button>
        </div>
        <button
          type="submit" disabled={busy}
          style={{
            width: '100%', padding: '12px 18px', fontSize: 15, fontWeight: 700,
            color: '#000', background: BTN_GRADIENT, border: 'none', borderRadius: 999,
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
            boxShadow: '0 4px 16px rgba(143,166,236,0.4)', fontFamily: 'inherit',
          }}
        >
          {busy ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Log in'}
        </button>
      </form>

      {error &&  <p style={{ color: '#c0392b', fontSize: 13, margin: 0, maxWidth: 300 }}>{error}</p>}
      {notice && <p style={{ color: ACCENT_DARK, fontSize: 13, margin: 0, maxWidth: 300 }}>{notice}</p>}

      {mode === 'signin' && (
        <button onClick={openReset} disabled={busy}
          style={{ background: 'none', border: 'none', color: SOFT_TEXT, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
          Forgot password?
        </button>
      )}
      </div>
      )}

      {mode !== 'reset' && (
      <p style={{ color: SOFT_TEXT, fontSize: 13, margin: 0, position: 'relative', zIndex: 1 }}>
        {mode === 'signup' ? 'Already have an account?' : 'New here?'}{' '}
        <button
          onClick={() => { setMode(mode === 'signup' ? 'signin' : 'signup'); setError(null); setNotice(null); }}
          style={{ background: 'none', border: 'none', color: OB_PINK_TEXT, textDecoration: 'underline', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          {mode === 'signup' ? 'Log in' : 'Create one'}
        </button>
      </p>
      )}

      <p style={{ color: SOFT_TEXT, fontSize: 11, margin: 0, maxWidth: 280, lineHeight: 1.5, position: 'relative', zIndex: 1 }}>
        By continuing, you agree to our{' '}
        <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: OB_INK, fontWeight: 600 }}>Terms</a>
        {' '}and{' '}
        <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: OB_INK, fontWeight: 600 }}>Privacy Policy</a>.
      </p>
    </div>,
    document.body
  );
}
